import React, { useState, useEffect, useMemo } from 'react';
import { Search, Shield, Zap, Terminal, Database, Eye, Lock, HardDrive, AlertTriangle, Server, Activity, X, Cloud, Key } from 'lucide-react';

// Define types for our data structures
type Tool = {
  name: string;
  description: string;
};

type ToolCategory = {
  category: string;
  icon: JSX.Element;
  description: string;
  tools: Tool[];
};

type AttackScenario = {
  name: string;
  description: string;
  redTeamTools: string[];
  blueTeamTools: string[];
  details: string;
};

type ExpandedScenarios = {
  [key: string]: boolean;
};

const CybersecurityToolsShowcase = () => {
  const [activeTab, setActiveTab] = useState(0);

  // Red Team Tools data - organized by categories with 2026 updates
  const redTeamTools = useMemo<ToolCategory[]>(() => [
    {
      category: "Reconnaissance",
      icon: <Search className="text-red-600" size={24} />,
      description: "Tools used to gather information about the target's infrastructure, network, and systems.",
      tools: [
        { name: "Shodan", description: "Search engine for Internet-connected devices and IoT" },
        { name: "Recon-ng", description: "Web reconnaissance framework with modular design" },
        { name: "theHarvester", description: "Email, subdomain, and people harvester for OSINT" },
        { name: "OSINT Framework", description: "Collection of OSINT tools for passive information gathering" },
        { name: "SpiderFoot", description: "Automated OSINT collection and reconnaissance tool" },
        { name: "Amass", description: "In-depth DNS enumeration and network mapping" },
        { name: "Maltego", description: "Interactive data mining tool for link analysis and visualization" },
        { name: "Netlas", description: "Internet intelligence platform for attack surface discovery and asset reconnaissance" }
      ]
    },
    {
      category: "Exploitation",
      icon: <Zap className="text-red-600" size={24} />,
      description: "Tools used to exploit vulnerabilities in systems and applications.",
      tools: [
        { name: "Metasploit", description: "Comprehensive penetration testing framework" },
        { name: "PowerShell Empire", description: "Post-exploitation framework with PowerShell agents" },
        { name: "Cobalt Strike", description: "Advanced threat emulation and red team operations platform" },
        { name: "Burp Suite", description: "Web vulnerability scanner and web application security testing" },
        { name: "ExploitDB", description: "Archive of public exploits and vulnerable software" },
        { name: "CALDERA", description: "Automated adversary emulation system from MITRE" },
        { name: "Mythic", description: "Command and control framework for payload development" },
        { name: "Nuclei", description: "Fast, template-based vulnerability scanner for web, network, and cloud targets" },
        { name: "CAI (Cybersecurity AI)", description: "AI-driven framework for security testing and bug bounty automation" }
      ]
    },
    {
      category: "Privilege Escalation",
      icon: <Terminal className="text-red-600" size={24} />,
      description: "Tools used to gain higher-level access within a system.",
      tools: [
        { name: "PowerUp", description: "PowerShell tool for Windows privilege escalation checks" },
        { name: "PEASS Suite", description: "Privilege escalation tools for Windows/Linux/macOS" },
        { name: "Rubeus", description: "Kerberos interaction and abuse toolkit" },
        { name: "BloodHound", description: "Active Directory visualization and attack path finding" },
        { name: "UACMe", description: "Windows User Account Control bypass methods collection" },
        { name: "Snaffler", description: "Advanced credential scanner for Active Directory environments" },
        { name: "SharpUp", description: ".NET port of PowerUp for Windows privilege escalation" },
        { name: "Mystique", description: "PowerShell tool for Kerberos S4U extensions abuse" }
      ]
    },
    {
      category: "Persistence",
      icon: <HardDrive className="text-red-600" size={24} />,
      description: "Tools used to maintain access to compromised systems.",
      tools: [
        { name: "Covenant", description: ".NET command and control framework" },
        { name: "SILENTTRINITY", description: "Post-exploitation agent using Python and IronPython" },
        { name: "Pupy", description: "Cross-platform remote administration and post-exploitation tool" },
        { name: "Empire", description: "Post-exploitation framework with modules across OS platforms" },
        { name: "Meterpreter", description: "Advanced payload for Metasploit with extensive functionality" },
        { name: "Sliver", description: "Cross-platform adversary emulation/red team framework" },
        { name: "Merlin", description: "Cross-platform post-exploitation HTTP/2 C2 tool" },
        { name: "Havoc", description: "Modern and malleable post-exploitation command and control framework" }
      ]
    },
    {
      category: "Data Exfiltration",
      icon: <Database className="text-red-600" size={24} />,
      description: "Tools used to extract and transfer data from compromised systems.",
      tools: [
        { name: "DNSExfiltrator", description: "Data exfiltration over DNS protocol" },
        { name: "Cloakify", description: "Data exfiltration toolkit for evading DLP/MLS devices" },
        { name: "PyExfil", description: "Python data exfiltration toolkit with multiple protocols" },
        { name: "Tunna", description: "Set of tools to tunnel traffic over HTTP" },
        { name: "egress-assess", description: "Tool to test egress data detection capabilities" },
        { name: "PowerSploit", description: "PowerShell post-exploitation framework with exfiltration and persistence modules" },
        { name: "DET", description: "Data Exfiltration Toolkit using multiple protocols" },
        { name: "ExMatter", description: "Modern exfiltration tool with encryption capabilities" }
      ]
    },
    {
      category: "AI-Enhanced Tools",
      icon: <Activity className="text-red-600" size={24} />,
      description: "Modern tools leveraging AI for red team operations.",
      tools: [
        { name: "Garak", description: "Automated red teaming tool for testing ML model safety and detecting LLM vulnerabilities" },
        { name: "PyRIT", description: "Microsoft's Python Risk Identification Toolkit for AI red teaming and LLM safety evaluation" },
        { name: "PromptMap", description: "Automated prompt injection and jailbreak testing tool for large language models" },
        { name: "AI-exploits", description: "Collection of exploits and proof-of-concept attacks for AI system security testing" },
        { name: "HiddenLayer", description: "AI/ML security testing and adversarial attack simulation platform" },
        { name: "DeepPhish", description: "AI-powered phishing content generator for social engineering simulations" },
        { name: "LLM-VM", description: "Language model vulnerability mapper for API security testing" },
        { name: "CIPHER", description: "AI-guided penetration testing assistant for structured offensive workflows" }
      ]
    },
    {
      category: "Cloud-Native Attack Tools",
      icon: <Cloud className="text-red-600" size={24} />,
      description: "Tools specifically designed for exploiting cloud infrastructure and services.",
      tools: [
        { name: "CloudGoat", description: "AWS vulnerable-by-design environment for security training" },
        { name: "TerraGoat", description: "Terraform vulnerable-by-design templates for security testing" },
        { name: "Pacu", description: "Open-source AWS exploitation framework for penetration testing" },
        { name: "kubehunter", description: "Kubernetes penetration testing tool for cluster security" },
        { name: "KICS", description: "Infrastructure-as-Code security scanner for pre-deployment testing" },
        { name: "Trivy Cloud", description: "Multi-cloud security scanner for misconfigurations and vulnerabilities" },
        { name: "SkyArk", description: "Cloud environments privileges discovery and assessment tool" },
        { name: "AzureHound", description: "Attack path mapping for Microsoft Azure environments" }
      ]
    }
  ], []);

  // Blue Team Tools data - organized by categories with 2026 updates
  const blueTeamTools = useMemo<ToolCategory[]>(() => [
    {
      category: "Monitoring & SIEM",
      icon: <Eye className="text-blue-600" size={24} />,
      description: "Tools used for monitoring, collecting, and analyzing security events.",
      tools: [
        { name: "Splunk", description: "Security Information and Event Management platform with advanced analytics" },
        { name: "ELK Stack", description: "Elasticsearch, Logstash, and Kibana for comprehensive log analysis" },
        { name: "OSSIM", description: "Open Source Security Information Management with event correlation" },
        { name: "Graylog", description: "Log management and analysis for centralized logging" },
        { name: "Wazuh", description: "Security monitoring, vulnerability detection, and incident response" },
        { name: "QRadar", description: "Enterprise SIEM solution with threat intelligence integration" },
        { name: "AlienVault USM", description: "Unified security management with SIEM functionality" },
        { name: "Splunk SOAR", description: "Security Orchestration, Automation and Response platform" }
      ]
    },
    {
      category: "Intrusion Detection",
      icon: <AlertTriangle className="text-blue-600" size={24} />,
      description: "Tools used to detect unauthorized access or attacks.",
      tools: [
        { name: "Snort", description: "Network intrusion detection system with extensive rule sets" },
        { name: "Suricata", description: "High-performance network IDS, IPS, and monitoring engine" },
        { name: "OSSEC", description: "Host-based intrusion detection system for comprehensive monitoring" },
        { name: "Security Onion", description: "Network security monitoring distribution with multiple tools" },
        { name: "Zeek (Bro)", description: "Network analysis framework for traffic inspection" },
        { name: "Maltrail", description: "Malicious traffic detection system using multiple sources" },
        { name: "RITA", description: "Real Intelligence Threat Analytics for detecting C2 infrastructure" },
        { name: "Sagan", description: "Real-time log analysis and correlation engine" }
      ]
    },
    {
      category: "Defense Tools",
      icon: <Shield className="text-blue-600" size={24} />,
      description: "Tools used to defend against attacks and secure systems.",
      tools: [
        { name: "Fail2Ban", description: "Intrusion prevention framework for brute force protection" },
        { name: "ModSecurity", description: "Web application firewall for application layer protection" },
        { name: "OpenVAS", description: "Vulnerability assessment system for scanning and remediation" },
        { name: "YARA", description: "Pattern matching tool for malware detection and classification" },
        { name: "pfSense", description: "Open-source firewall/router for network segmentation" },
        { name: "Crowdsec", description: "Collaborative security platform for threat detection" },
        { name: "BT3", description: "Blue Team Training Toolkit for security drills" },
        { name: "DumpsterFire", description: "Tool for building repeatable security event chains" },
        { name: "SCYTHE", description: "Adversary emulation platform for defensive testing" }
      ]
    },
    {
      category: "Forensics & IR",
      icon: <Lock className="text-blue-600" size={24} />,
      description: "Tools used for incident response and forensic analysis.",
      tools: [
        { name: "Volatility", description: "Memory forensics framework for detailed analysis" },
        { name: "TheHive", description: "Incident response platform for security operations" },
        { name: "GRR", description: "Incident response framework for remote live forensics" },
        { name: "CAINE", description: "Computer forensics Linux distribution with extensive toolset" },
        { name: "Autopsy", description: "Digital forensics platform for drive investigation" },
        { name: "Velociraptor", description: "Advanced digital forensics and incident response tool" },
        { name: "DFIR ORC", description: "Forensic collection tool for Windows systems" },
        { name: "CyLR", description: "Fast disk acquisition tool for incident response" },
        { name: "OSForensics", description: "Comprehensive computer forensics software" }
      ]
    },
    {
      category: "Threat Intelligence",
      icon: <Activity className="text-blue-600" size={24} />,
      description: "Tools used for gathering intelligence about threats and attackers.",
      tools: [
        { name: "MISP", description: "Malware Information Sharing Platform for threat sharing" },
        { name: "OpenCTI", description: "Open Cyber Threat Intelligence Platform for knowledge management" },
        { name: "ThreatConnect", description: "Threat intelligence platform with analytics" },
        { name: "IntelMQ", description: "Solution for CERTs to process security feeds into actionable intelligence" },
        { name: "MITRE ATT&CK", description: "Knowledge base of adversary tactics and techniques" },
        { name: "ThreatHunting", description: "Splunk app for threat hunting with predefined dashboards" },
        { name: "HELK", description: "Hunting ELK stack with advanced analytics capabilities" },
        { name: "Yeti", description: "Platform for organizing and sharing threat intelligence" }
      ]
    },
    {
      category: "AI-Enhanced Defense",
      icon: <Server className="text-blue-600" size={24} />,
      description: "Advanced tools using AI for blue team operations.",
      tools: [
        { name: "Darktrace", description: "Enterprise immune system using AI for autonomous threat detection and response" },
        { name: "Vectra AI", description: "AI-driven network threat detection and response platform" },
        { name: "Cybereason", description: "AI-powered endpoint detection and response platform" },
        { name: "Picus Security", description: "Security control validation and breach and attack simulation with AI analytics" },
        { name: "CrowdStrike Falcon", description: "Next-generation endpoint protection with AI-driven threat hunting and prevention" },
        { name: "Lakera Guard", description: "Real-time LLM security API protecting against prompt injection, jailbreaks, and data leakage" },
        { name: "LLM-Guard", description: "Open-source toolkit for input/output sanitization and safety guardrails on LLM applications" },
        { name: "NeMo Guardrails", description: "NVIDIA's open-source framework for adding safety, security, and dialogue controls to LLMs" },
        { name: "SentinelOne Singularity", description: "AI-powered XDR platform with autonomous threat detection across endpoint, cloud, and identity" }
      ]
    },
    {
      category: "Zero Trust Security Tools",
      icon: <Key className="text-blue-600" size={24} />,
      description: "Tools implementing zero trust security principles for modern networks and applications.",
      tools: [
        { name: "BeyondCorp Enterprise", description: "Google's zero trust security platform for secure access" },
        { name: "Zscaler Private Access", description: "Zero trust network access service for application security" },
        { name: "Twingate", description: "Modern zero trust network solution replacing traditional VPNs" },
        { name: "Okta Verify", description: "Context-aware authentication service for zero trust access" },
        { name: "CrowdStrike Zero Trust", description: "Endpoint-focused zero trust platform with identity protection" },
        { name: "Tailscale", description: "Zero-config VPN for secure network access based on WireGuard" },
        { name: "Perimeter 81", description: "Zero trust network as a service with SASE capabilities" },
        { name: "Banyan Security", description: "Zero trust remote access platform with least-privilege access" }
      ]
    }
  ], []);

  // Detailed Attack Scenarios with latest techniques
  const attackScenarios = useMemo<AttackScenario[]>(() => [
    {
      name: "Advanced Phishing Campaign",
      description: "Attacker leverages sophisticated phishing techniques to harvest credentials or deliver malware, often using AI to generate convincing content.",
      redTeamTools: [
        "Social Engineering Toolkit (SET)", 
        "GoPhish", 
        "Evilginx2", 
        "DeepPhish", 
        "SpearPhisher"
      ],
      blueTeamTools: [
        "Email Security Gateways", 
        "DMARC/DKIM/SPF Implementation", 
        "AI-based Phishing Detection", 
        "User Security Awareness Training", 
        "YARA Rules"
      ],
      details: "Modern phishing attacks now utilize AI to create highly convincing emails that bypass traditional filters. Red teams simulate these attacks using tools that can generate personalized content based on OSINT data, while blue teams employ AI-driven detection systems that analyze behavioral patterns rather than just content signatures."
    },
    {
      name: "Ransomware Attack with Double Extortion",
      description: "Advanced ransomware attack that both encrypts data and exfiltrates sensitive information, threatening to publish stolen data if ransom isn't paid.",
      redTeamTools: [
        "Metasploit", 
        "PowerShell Empire", 
        "Mimikatz", 
        "Cobalt Strike", 
        "DNSExfiltrator"
      ],
      blueTeamTools: [
        "EDR Solutions", 
        "Immutable Backup Systems", 
        "Network Segmentation", 
        "YARA Rules", 
        "Behavioral Analytics"
      ],
      details: "Modern ransomware operations now employ a double extortion model. After gaining initial access often through phishing or exploiting vulnerabilities, attackers establish persistence, perform lateral movement to identify valuable data, exfiltrate sensitive information before encryption, and then deploy ransomware payloads."
    },
    {
      name: "Supply Chain Attack",
      description: "Compromising trusted third-party software or hardware to gain access to multiple organizations simultaneously.",
      redTeamTools: [
        "Dependency Confusion Tools", 
        "SolarWinds Attack Emulator", 
        "Malicious Package Injectors", 
        "Code Signing Exploits", 
        "SLIVER C2"
      ],
      blueTeamTools: [
        "Software Composition Analysis", 
        "Vendor Risk Management", 
        "Integrity Verification", 
        "Behavioral Monitoring", 
        "Network Traffic Analysis"
      ],
      details: "Supply chain attacks target the trusted relationships between organizations and their vendors. Red teams simulate these by identifying dependencies in the software supply chain and demonstrating how compromised packages could be introduced. Blue teams use software composition analysis to monitor dependencies and implement integrity verification checks."
    },
    {
      name: "Cloud Infrastructure Attack",
      description: "Exploiting misconfigurations and vulnerabilities in cloud services and infrastructure to gain unauthorized access.",
      redTeamTools: [
        "Pacu", 
        "ScoutSuite", 
        "CloudSploit", 
        "Cartography", 
        "Prowler"
      ],
      blueTeamTools: [
        "Cloud Security Posture Management", 
        "Identity and Access Management", 
        "Cloud Workload Protection", 
        "Cloud-native SIEM", 
        "Infrastructure as Code Scanning"
      ],
      details: "As organizations migrate to the cloud, attackers increasingly target misconfigurations and excessive permissions. Red teams use specialized cloud pentesting tools to discover these issues while blue teams implement continuous security validation and least-privilege access controls to mitigate risks."
    },
    {
      name: "Advanced Persistent Threat (APT)",
      description: "Sophisticated, long-term attack campaign typically conducted by nation-state actors or organized crime groups.",
      redTeamTools: [
        "CALDERA", 
        "Cobalt Strike", 
        "Empire", 
        "Mythic", 
        "Havoc"
      ],
      blueTeamTools: [
        "Threat Hunting Platforms", 
        "EDR with XDR Integration", 
        "HELK", 
        "MITRE ATT&CK Framework", 
        "Threat Intelligence Platforms"
      ],
      details: "APT simulations test an organization's ability to detect and respond to sophisticated, long-term campaigns. Red teams establish covert persistence, employ living-off-the-land techniques, and use encrypted command and control channels, while blue teams utilize threat hunting and behavioral analysis to identify subtle indicators of compromise."
    },
    {
      name: "IoT/OT Network Compromise",
      description: "Targeting vulnerable Internet of Things or Operational Technology devices to pivot into critical infrastructure.",
      redTeamTools: [
        "Shodan", 
        "Firmwalker", 
        "EmbedOS", 
        "Attify Framework", 
        "Firmware-mod-kit"
      ],
      blueTeamTools: [
        "OT Network Monitoring", 
        "Asset Discovery and Management", 
        "IoT Security Platforms", 
        "Network Segmentation", 
        "ICS-specific IDS"
      ],
      details: "As industrial systems and IoT devices proliferate, they present new attack surfaces. Red teams leverage specialized tools to identify vulnerable devices and demonstrate potential impacts, while blue teams implement dedicated monitoring solutions and proper network segmentation to protect critical operational technology."
    },
    {
      name: "AI Security Testing",
      description: "Testing the security of AI/ML systems against adversarial attacks and model tampering.",
      redTeamTools: [
        "Garak", 
        "AI-exploits", 
        "HiddenLayer", 
        "Adversarial Robustness Toolbox", 
        "LLM-VM"
      ],
      blueTeamTools: [
        "ML Security Scanning", 
        "Input Validation Systems", 
        "Model Monitoring", 
        "Training Data Protection", 
        "AI Governance Framework"
      ],
      details: "As AI systems become more prevalent, they introduce new attack vectors. Red teams now test for prompt injection, model evasion, and data poisoning attacks, while blue teams implement input sanitization, monitoring for anomalous model behavior, and security controls specific to machine learning infrastructure."
    },
    {
      name: "Multi-Cloud Lateral Movement",
      description: "Attackers exploit interconnected cloud services to move laterally between AWS, Azure, and GCP environments.",
      redTeamTools: [
        "Pacu", 
        "Gato", 
        "ScoutSuite", 
        "AzureHound",
        "StormSpotter",
        "MicroBurst"
      ],
      blueTeamTools: [
        "Cloud Security Posture Management", 
        "Multi-cloud IAM Solutions", 
        "Cloud Workload Segmentation",
        "Cloud Access Security Brokers",
        "Prisma Cloud"
      ],
      details: "In multi-cloud lateral movement attacks, adversaries exploit interconnected identity systems and cross-cloud trust relationships. After gaining initial access to one cloud provider, attackers pivot through trusted connections to access other cloud environments. This scenario tests an organization's ability to maintain consistent security controls across multiple cloud providers and detect anomalous cross-cloud activity patterns."
    },
    {
      name: "AI/ML System Compromise",
      description: "Targeted attacks against ML/AI infrastructure including prompt injection, model poisoning, and data extraction.",
      redTeamTools: [
        "GPTFuzz", 
        "Garak", 
        "AdvBox", 
        "TextAttack",
        "MLInjection",
        "ModelSteal"
      ],
      blueTeamTools: [
        "Robust ML", 
        "LLM-Guard", 
        "TruEra", 
        "AI Guardian",
        "Adversarial Robustness Toolkit",
        "NeMo Guardrails"
      ],
      details: "AI/ML system compromises target the unique vulnerabilities in machine learning pipelines. Attacks include prompt injection to bypass guardrails, data poisoning during model training, extraction of sensitive information through carefully crafted inputs, and jailbreaking attempts to circumvent safety measures. Blue teams employ specialized monitoring for AI systems and implement input sanitization along with model behavior analysis."
    },
    {
      name: "API Chain Exploitation",
      description: "Chaining multiple low-severity API vulnerabilities to achieve high-impact compromise of interconnected services.",
      redTeamTools: [
        "Postman Intruder", 
        "Akto", 
        "APIClarity", 
        "TnT-Fuzzer",
        "APIKit",
        "APIFuzz"
      ],
      blueTeamTools: [
        "API Gateway Security", 
        "APISec", 
        "Traceable AI", 
        "42Crunch",
        "Salt Security",
        "Noname API Security"
      ],
      details: "API chain exploitation involves discovering and connecting multiple seemingly minor vulnerabilities across different API endpoints to achieve significant security breaches. This technique exploits the interconnected nature of modern microservices architectures. Defenders implement API security gateways that analyze the full API attack surface and monitor for unusual sequences of API calls that could indicate chained exploitation attempts."
    },
    {
      name: "Agentic AI System Exploitation",
      description: "Targeting autonomous AI agents and LLM-powered workflows to hijack tool calls, exfiltrate data through prompt injection, or cause agents to take unauthorized real-world actions.",
      redTeamTools: [
        "Garak",
        "PyRIT",
        "PromptMap",
        "Skeleton Key (prompt override)",
        "PAIR (Prompt Automatic Iterative Refinement)"
      ],
      blueTeamTools: [
        "Lakera Guard",
        "LLM-Guard",
        "NeMo Guardrails",
        "PromptShield (Azure AI)",
        "AI Governance & Audit Logging"
      ],
      details: "As organizations deploy autonomous AI agents that browse the web, execute code, and call external APIs, attackers are targeting the agent's prompt context as an attack vector. Indirect prompt injection — embedding malicious instructions in content the agent retrieves — can cause it to exfiltrate user data, call unintended APIs, or bypass guardrails entirely. Red teams simulate these attacks using iterative prompt refinement and tool-call hijacking techniques. Blue teams respond with input/output sanitization layers, strict tool-call allowlists, scoped credentials for agents, and behavioral monitoring to detect anomalous agent actions before they reach external systems."
    }
  ], []);

  // Add search functionality
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredRedTools, setFilteredRedTools] = useState<ToolCategory[]>(redTeamTools);
  const [filteredBlueTools, setFilteredBlueTools] = useState<ToolCategory[]>(blueTeamTools);
  const [filteredScenarios, setFilteredScenarios] = useState<AttackScenario[]>(attackScenarios);
  const [expandedScenarios, setExpandedScenarios] = useState<ExpandedScenarios>({});
  
  // Handle search
  useEffect(() => {
    if (searchTerm) {
      // Filter red team tools
      const redFiltered: ToolCategory[] = redTeamTools
        .map(category => {
          const filteredTools = category.tools.filter(tool => 
            tool.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
            tool.description.toLowerCase().includes(searchTerm.toLowerCase())
          );
          return filteredTools.length > 0 ? {...category, tools: filteredTools} : null;
        })
        .filter((item): item is ToolCategory => item !== null);
      
      // Filter blue team tools
      const blueFiltered: ToolCategory[] = blueTeamTools
        .map(category => {
          const filteredTools = category.tools.filter(tool => 
            tool.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
            tool.description.toLowerCase().includes(searchTerm.toLowerCase())
          );
          return filteredTools.length > 0 ? {...category, tools: filteredTools} : null;
        })
        .filter((item): item is ToolCategory => item !== null);
      
      // Filter attack scenarios
      const scenariosFiltered = attackScenarios.filter(scenario => 
        scenario.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        scenario.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        scenario.redTeamTools.some(tool => tool.toLowerCase().includes(searchTerm.toLowerCase())) ||
        scenario.blueTeamTools.some(tool => tool.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      
      setFilteredRedTools(redFiltered);
      setFilteredBlueTools(blueFiltered);
      setFilteredScenarios(scenariosFiltered);
    } else {
      // Reset to original lists if search is cleared
      setFilteredRedTools(redTeamTools);
      setFilteredBlueTools(blueTeamTools);
      setFilteredScenarios(attackScenarios);
    }
  }, [searchTerm, redTeamTools, blueTeamTools, attackScenarios]);
  
  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
  };
  
  // Handle expanding/collapsing scenarios
  const toggleScenario = (scenarioName: string) => {
    setExpandedScenarios(prev => ({
      ...prev,
      [scenarioName]: !prev[scenarioName]
    }));
  };
  
  // Handle tab change
  const handleTabChange = (index: number) => {
    setActiveTab(index);
  };

  // Render tool category
  const renderToolCategory = (category: ToolCategory) => {
    return (
      <div key={category.category} className="mb-8">
        <div className="flex items-center mb-2">
          {category.icon}
          <h3 className="text-xl font-bold ml-2">{category.category}</h3>
        </div>
        <p className="text-gray-600 mb-3">{category.description}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {category.tools.map((tool) => (
            <div key={tool.name} className="border border-stone-200 p-3 rounded-lg shadow-sm bg-stone-50">
              <h4 className="font-semibold">{tool.name}</h4>
              <p className="text-sm text-gray-500">{tool.description}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render attack scenario with expanded details
  const renderAttackScenario = (scenario: AttackScenario) => {
    const isExpanded = expandedScenarios[scenario.name] || false;
    
    return (
      <div key={scenario.name} className="mb-6 border-b pb-6">
        <h3 className="text-xl font-bold mb-2">{scenario.name}</h3>
        <p className="text-gray-600 mb-3">{scenario.description}</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
          <div>
            <h4 className="flex items-center text-lg font-semibold text-red-600 mb-2">
              <Zap size={20} className="mr-2" /> Red Team Tools
            </h4>
            <ul className="list-disc pl-5">
              {scenario.redTeamTools.map((tool) => (
                <li key={tool} className="mb-1">{tool}</li>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="flex items-center text-lg font-semibold text-blue-600 mb-2">
              <Shield size={20} className="mr-2" /> Blue Team Defense
            </h4>
            <ul className="list-disc pl-5">
              {scenario.blueTeamTools.map((tool) => (
                <li key={tool} className="mb-1">{tool}</li>
              ))}
            </ul>
          </div>
        </div>
        
        {scenario.details && (
          <>
            <div className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-96' : 'max-h-0'}`}>
              <div className="bg-stone-100 p-4 rounded-lg mb-3">
                <h4 className="font-semibold mb-2">Technical Details:</h4>
                <p className="text-gray-700">{scenario.details}</p>
              </div>
            </div>
            
            <button 
              onClick={() => toggleScenario(scenario.name)}
              className="text-purple-600 hover:text-purple-800 text-sm font-medium flex items-center"
            >
              {isExpanded ? 'Show Less' : 'Show More Details'}
              <svg 
                className={`ml-1 w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto p-4">
      <div className="text-center mb-6">
        <h1 className="text-3xl font-bold mb-2">Cybersecurity Red Team & Blue Team Tools</h1>
        <p className="text-gray-600">
          Interactive showcase of tools used by offensive (Red Team) and defensive (Blue Team) security professionals
        </p>
      </div>

      {/* Search bar */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={18} className="text-gray-400" />
          </div>
          <input 
            type="text" 
            className="w-full pl-10 pr-10 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-stone-50"
            placeholder="Search for tools, techniques, or attack vectors..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
          {searchTerm && (
            <button 
              className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
              onClick={clearSearch}
            >
              <X size={18} className="text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
        {searchTerm && (
          <div className="mt-2 text-sm text-gray-500">
            Showing results for "{searchTerm}" • {
              (activeTab === 0 ? filteredRedTools.flatMap(cat => cat.tools).length : 
               activeTab === 1 ? filteredBlueTools.flatMap(cat => cat.tools).length : 
               filteredScenarios.length)} matches
          </div>
        )}
      </div>

      <div className="mb-8">
        <div className="border-b">
          <div className="flex">
            <button
              className={`px-4 py-2 font-medium ${activeTab === 0 ? 'text-red-600 border-b-2 border-red-600' : 'text-gray-500'}`}
              onClick={() => handleTabChange(0)}
            >
              <div className="flex items-center">
                <Zap size={18} className="mr-2" />
                Red Team Tools
              </div>
            </button>
            <button
              className={`px-4 py-2 font-medium ${activeTab === 1 ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
              onClick={() => handleTabChange(1)}
            >
              <div className="flex items-center">
                <Shield size={18} className="mr-2" />
                Blue Team Tools
              </div>
            </button>
            <button
              className={`px-4 py-2 font-medium ${activeTab === 2 ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500'}`}
              onClick={() => handleTabChange(2)}
            >
              <div className="flex items-center">
                <Server size={18} className="mr-2" />
                Attack Scenarios
              </div>
            </button>
          </div>
        </div>

        <div className="mt-6">
          {activeTab === 0 && (
            <div>
              <div className="bg-red-50 p-4 rounded-lg mb-6">
                <h2 className="text-2xl font-bold text-red-700 mb-2">Red Team Tools</h2>
                <p className="text-gray-700">
                  Red Team tools are used by offensive security professionals to simulate real-world attacks and identify vulnerabilities in systems and networks. These tools help organizations understand their security weaknesses from an attacker's perspective.
                </p>
              </div>
              {(searchTerm ? filteredRedTools : redTeamTools).map(renderToolCategory)}
              {searchTerm && filteredRedTools.length === 0 && (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-2">No red team tools match your search</div>
                  <button 
                    className="text-purple-600 hover:underline flex items-center justify-center mx-auto"
                    onClick={clearSearch}
                  >
                    <X size={16} className="mr-1" />
                    Clear search
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 1 && (
            <div>
              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <h2 className="text-2xl font-bold text-blue-700 mb-2">Blue Team Tools</h2>
                <p className="text-gray-700">
                  Blue Team tools are used by defensive security professionals to protect, detect, and respond to security threats. These tools help organizations monitor their systems, identify suspicious activities, and respond to security incidents.
                </p>
              </div>
              {(searchTerm ? filteredBlueTools : blueTeamTools).map(renderToolCategory)}
              {searchTerm && filteredBlueTools.length === 0 && (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-2">No blue team tools match your search</div>
                  <button 
                    className="text-purple-600 hover:underline flex items-center justify-center mx-auto"
                    onClick={clearSearch}
                  >
                    <X size={16} className="mr-1" />
                    Clear search
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 2 && (
            <div>
              <div className="bg-purple-50 p-4 rounded-lg mb-6">
                <h2 className="text-2xl font-bold text-purple-700 mb-2">Attack Scenarios</h2>
                <p className="text-gray-700">
                  This section illustrates how Red Team tools are used to simulate attacks and how Blue Team tools can detect and mitigate these threats. Understanding these scenarios helps build more robust security defenses.
                </p>
              </div>
              {(searchTerm ? filteredScenarios : attackScenarios).map(renderAttackScenario)}
              {searchTerm && filteredScenarios.length === 0 && (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-2">No attack scenarios match your search</div>
                  <button 
                    className="text-purple-600 hover:underline flex items-center justify-center mx-auto"
                    onClick={clearSearch}
                  >
                    <X size={16} className="mr-1" />
                    Clear search
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      <div className="text-center text-sm text-gray-500 mt-8">
        <p>Based on cybersecurity industry standard tools • Sourced from public security resources</p>
        <p>Created to visualize Red Team and Blue Team tool categories and modern cybersecurity techniques</p>
        <p className="text-xs mt-2">Last updated: May 2026 • Information provided for educational purposes only</p>
        <div className="flex justify-center space-x-8 mt-4 text-gray-600">
          <a href="https://tooling.hackernoob.tips/" target="_blank" rel="noopener noreferrer" className="hover:text-purple-600 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" className="mr-1">
              <path d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5z"/>
              <path d="M9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5L9.5 0zm0 1v2A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
            </svg>
            Security Tools Directory
          </a>
          <a href="https://directory.cisomarketplace.com/" target="_blank" rel="noopener noreferrer" className="hover:text-purple-600 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" className="mr-1">
              <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.25-14.75v1.5a.25.25 0 0 1-.5 0v-1.5a.25.25 0 0 1 .5 0zm0 12v1.5a.25.25 0 1 1-.5 0v-1.5a.25.25 0 1 1 .5 0zM4.5 1.938a.25.25 0 0 1 .342.091l.75 1.3a.25.25 0 0 1-.434.25l-.75-1.3a.25.25 0 0 1 .092-.341zm6 10.392a.25.25 0 0 1 .341.091l.75 1.3a.25.25 0 1 1-.432.25l-.75-1.3a.25.25 0 0 1 .091-.341zM1.938 4.5a.25.25 0 0 1 .091.342l-1.3.75a.25.25 0 0 1-.25-.432l1.3-.75a.25.25 0 0 1 .159-.01zm12.03 6a.25.25 0 0 1 .091.342l-1.3.75a.25.25 0 1 1-.25-.432l1.3-.75a.25.25 0 0 1 .159-.01zM1.5 8.25a.25.25 0 0 1 .25-.25h1.5a.25.25 0 1 1 0 .5h-1.5a.25.25 0 0 1-.25-.25zm12 0a.25.25 0 0 1 .25-.25h1.5a.25.25 0 1 1 0 .5h-1.5a.25.25 0 0 1-.25-.25zM1.938 11.5a.25.25 0 0 1-.342.091l-1.3-.75a.25.25 0 0 1 .25-.432l1.3.75a.25.25 0 0 1 .092.341zm12.03-6a.25.25 0 0 1-.341.091l-1.3-.75a.25.25 0 0 1 .25-.432l1.3.75a.25.25 0 0 1 .091.341zM4.5 14.061a.25.25 0 0 1-.342-.091l-.75-1.3a.25.25 0 0 1 .434-.25l.75 1.3a.25.25 0 0 1-.092.341zm6-10.392a.25.25 0 0 1-.341-.091l-.75-1.3a.25.25 0 0 1 .432-.25l.75 1.3a.25.25 0 0 1-.091.341z"/>
            </svg>
            MSP/MSSP Directory
          </a>
          <a href="https://attack.mitre.org/matrices/enterprise/" target="_blank" rel="noopener noreferrer" className="hover:text-purple-600 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" className="mr-1">
              <path fillRule="evenodd" d="M1 11.5a.5.5 0 0 0 .5.5h11.793l-3.147 3.146a.5.5 0 0 0 .708.708l4-4a.5.5 0 0 0 0-.708l-4-4a.5.5 0 0 0-.708.708L13.293 11H1.5a.5.5 0 0 0-.5.5zm14-7a.5.5 0 0 1-.5.5H2.707l3.147 3.146a.5.5 0 1 1-.708.708l-4-4a.5.5 0 0 1 0-.708l4-4a.5.5 0 1 1 .708.708L2.707 4H14.5a.5.5 0 0 1 .5.5z"/>
            </svg>
            MITRE ATT&CK
          </a>
        </div>
        <p className="text-xs mt-4">Sponsored by <a href="https://www.hackernoob.tips/" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">hackernoob.tips</a></p>
      </div>
    </div>
  );
};

export default CybersecurityToolsShowcase;